<template>
  <div class="webboard-detail-page">
    <div class="d-flex justify-space-between mb-4">
      <div>
        <div class="page-title mb-2">รายละเอียดกระทู้</div>
        <div class="theme-color-eight">รายละเอียดกระทู้</div>
      </div>
    </div>

    <divcard class="mt-6" v-if="topic">
      <div class="pa-8">
        <div class="topic-head">
          <div class="topic-author-wrap">
            <div class="topic-avatar">{{ topic.authorInitial }}</div>
            <div>
              <div class="topic-author">@{{ topic.author }}</div>
              <div class="topic-date">{{ topic.dateText }}</div>
            </div>
          </div>

          <button class="delete-topic-button" type="button" @click="requestDeleteTopic">
            <Iconify
              icon="tabler:message-cancel"
              color="var(--theme-color-seven)"
              size="22px"
            />
            <span>แจ้งลบกระทู้</span>
          </button>
        </div>

        <div class="topic-title mt-6">{{ topic.title }}</div>
        <div class="topic-content mt-6" v-html="topic.content"></div>
      </div>
    </divcard>

    <div class="comment-label mt-8">คอมเมนต์</div>

    <divcard v-if="canComment" class="mt-4">
      <div class="pa-6">
        <div class="comment-editor-frame">
          <Wysiwyg
            :value="commentText"
            height="180px"
            @onChange="commentText = $event"
          />
        </div>

        <div class="d-flex justify-end gap-12 mt-4">
          <Button theme="2" @onClick="commentText = ''">ยกเลิก</Button>
          <Button :loading="commentLoading" theme="1" @onClick="submitComment">
            <div class="d-flex align-center gap-8">
              <Iconify
                icon="bxs:message-square-detail"
                color="#FFFFFF"
                size="16px"
              />
              <span>คอมเมนต์</span>
            </div>
          </Button>
        </div>
      </div>
    </divcard>

    <divcard v-else class="mt-4">
      <div class="pa-6 comment-disabled-state">
        กระทู้นี้ไม่สามารถแสดงความคิดเห็นได้
      </div>
    </divcard>

    <div class="comment-list mt-6">
      <CommentItem
        v-for="comment in comments"
        :key="comment.id"
        :comment="comment"
        :can-comment="canComment"
        :active-reply-id="activeReplyId"
        :reply-drafts="replyDrafts"
        :reply-loading-id="replyLoadingId"
        @open-reply="openReplyForm"
        @close-reply="closeReplyForm"
        @update-reply-text="updateReplyText"
        @submit-reply="submitReply"
      />

      <div v-if="!comments.length" class="empty-state">
        ยังไม่มีคอมเมนต์
      </div>
    </div>

    <div class="mt-6 d-flex justify-center">
      <v-pagination
        v-model="commentPaginate.current_page"
        :length="commentPaginate.last_page"
        :total-visible="5"
        color="var(--theme-color-seven)"
        @input="goToCommentPage"
      />
    </div>

    <v-dialog v-model="deleteDialog" max-width="420" persistent>
      <div class="delete-request-modal">
        <div class="delete-request-icon-wrap">
          <Iconify
            icon="mdi:forum-remove"
            color="var(--theme-color-seven)"
            size="60px"
          />
        </div>

        <div class="delete-request-title">แจ้งลบกระทู้</div>
        <div class="delete-request-subtitle">กรุณาระบุเหตุผลที่ต้องการแจ้งลบ</div>

        <TextArea
          :value="deleteReason"
          :disabled="deleteLoading"
          hide-details
          theme="3"
          height="120"
          placeholder="เหตุผลการแจ้งลบ"
          @onChange="deleteReason = $event"
        />

        <div class="d-flex justify-center gap-12 mt-6">
          <Button
            theme="2"
            min-width="110px"
            :disabled="deleteLoading"
            @onClick="closeDeleteDialog"
          >
            ยกเลิก
          </Button>
          <Button
            theme="1"
            min-width="110px"
            :loading="deleteLoading"
            @onClick="submitDeleteRequest"
          >
            ยืนยัน
          </Button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import CommentItem from "@/components/Webboard/CommentItem.vue";
import Button from "@/components/input/Button.vue";
import TextArea from "@/components/input/TextArea.vue";
import Wysiwyg from "@/components/input/Wysiwyg.vue";

export default {
  name: "WebboardDetail",
  components: {
    CommentItem,
    Button,
    TextArea,
    Wysiwyg,
  },
  data() {
    return {
      topic: null,
      comments: [],
      commentPaginate: {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      },
      commentText: "",
      commentLoading: false,
      activeReplyId: null,
      replyDrafts: {},
      replyLoadingId: null,
      deleteDialog: false,
      deleteReason: "",
      deleteLoading: false,
    };
  },
  computed: {
    canComment() {
      return (
        this.topic &&
        ["published", "delete_requested"].includes(this.topic.status)
      );
    },
  },
  created() {
    this.getTopic();
  },
  methods: {
    async getTopic(page = 1, trackView = true) {
      const res = await this.$apiFire("Webboard/show", {
        id: this.$route.params.id,
        comments_page: page,
        comments_per_page: this.commentPaginate.per_page,
        track_view: trackView,
      });

      if (!res) return;

      const topic = res.data;
      this.topic = {
        ...topic,
        author: this.getDisplayName(topic.user),
        authorInitial: this.getInitial(topic.user),
        dateText: this.formatDate(topic.created_at),
      };
      this.comments = topic.root_comments || [];
      this.activeReplyId = null;
      this.replyLoadingId = null;
      this.commentPaginate = {
        ...this.commentPaginate,
        ...(topic.comments_pagination || {}),
      };
    },
    async submitComment() {
      const text = (this.commentText || "").trim();

      if (!this.hasMeaningfulContent(text)) return;

      this.commentLoading = true;
      const res = await this.$apiFire("Webboard/comment", {
        topicId: this.$route.params.id,
        content: text,
      });
      this.commentLoading = false;

      if (!res) return;

      this.commentText = "";
      await this.getTopic(this.commentPaginate.current_page, false);
    },
    openReplyForm(commentId) {
      this.activeReplyId = commentId;
      this.replyDrafts = {
        ...this.replyDrafts,
        [commentId]: this.replyDrafts[commentId] || "",
      };
    },
    closeReplyForm(commentId) {
      if (this.replyLoadingId === commentId) return;
      this.activeReplyId = this.activeReplyId === commentId ? null : this.activeReplyId;
      this.replyDrafts = {
        ...this.replyDrafts,
        [commentId]: "",
      };
    },
    updateReplyText({ id, value }) {
      this.replyDrafts = {
        ...this.replyDrafts,
        [id]: value,
      };
    },
    async submitReply(comment) {
      const text = (this.replyDrafts[comment.id] || "").trim();

      if (!this.hasMeaningfulContent(text)) return;

      this.replyLoadingId = comment.id;
      const res = await this.$apiFire("Webboard/comment", {
        topicId: this.$route.params.id,
        content: text,
        parent_id: comment.id,
      });
      this.replyLoadingId = null;

      if (!res) return;

      this.activeReplyId = null;
      this.replyDrafts = {
        ...this.replyDrafts,
        [comment.id]: "",
      };
      await this.getTopic(this.commentPaginate.current_page, false);
    },
    async goToCommentPage(page) {
      await this.getTopic(page, false);
    },
    hasMeaningfulContent(content) {
      const html = (content || "").trim();

      if (!html) return false;

      const textOnly = html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
      const hasMedia = /<(img|video|iframe|embed|object|audio)\b/i.test(html);

      return Boolean(textOnly || hasMedia);
    },
    requestDeleteTopic() {
      this.deleteReason = "";
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      if (this.deleteLoading) return;
      this.deleteDialog = false;
      this.deleteReason = "";
    },
    async submitDeleteRequest() {
      const trimmedReason = (this.deleteReason || "").trim();

      if (!trimmedReason) {
        this.$store.commit("General/setModal", {
          title: "ข้อมูลไม่ถูกต้อง",
          text: "กรุณาระบุเหตุผลการแจ้งลบ",
          buttonText: "ตกลง",
          showCancel: false,
          theme: "warning",
        });
        return;
      }

      this.deleteLoading = true;
      const res = await this.$apiFire("Webboard/requestDelete", {
        id: this.$route.params.id,
        reason: trimmedReason,
      });
      this.deleteLoading = false;

      if (!res) return;

      this.deleteDialog = false;
      this.deleteReason = "";
      this.$store.commit("General/setModal", {
        title: "สำเร็จ",
        text: "ส่งคำขอแจ้งลบกระทู้เรียบร้อยแล้ว",
        buttonText: "ตกลง",
        showCancel: false,
      });

      this.$router.push({ name: "Webboard" });
    },
    getDisplayName(user) {
      if (!user) return "-";
      return user.name || [user.firstname, user.lastname].filter(Boolean).join(" ") || "-";
    },
    getInitial(user) {
      return (this.getDisplayName(user) || "W").toString().charAt(0).toUpperCase();
    },
    formatDate(date) {
      return this.$moment(date).add(543, "year").format("DD MMMM YYYY HH:mm");
    },
  },
};
</script>

<style scoped lang="scss">
.webboard-detail-page {
  min-height: calc(100vh - var(--header-height));
}

.topic-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.delete-topic-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 30px;
  border: 1.5px solid #f15a29;
  background: #ffffff;
  box-shadow: 1px 1px 10px 0 rgba(0, 0, 0, 0.25);
  color: var(--theme-color-seven);
  font-family: var(--font-bold-type);
  cursor: pointer;
}

.topic-author-wrap {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.topic-avatar {
  width: 30px;
  min-width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(180deg, #ff8a4c 0%, #f15a29 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-family: var(--font-bold-type);
}

.topic-author {
  font-size: 12px;
  font-family: var(--font-bold-type);
  color: var(--theme-color-two);
}

.topic-date {
  font-size: 11px;
  color: var(--theme-color-eight);
}

.topic-title {
  font-size: 28px;
  font-family: var(--font-bold-type);
  color: var(--theme-color-two);
}

.topic-content {
  color: var(--theme-color-two);
  line-height: 1.9;
}

.comment-label {
  text-align: center;
  color: var(--theme-color-eight);
  font-family: var(--font-bold-type);
}

.topic-stats {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stat-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 999px;
  background: linear-gradient(180deg, #ffb241 0%, #f7941d 100%);
  color: #fff;
  font-size: 12px;
  font-family: var(--font-bold-type);
  box-shadow: 0 6px 14px rgba(241, 90, 41, 0.18);
}

.comment-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.empty-state {
  padding: 24px;
  text-align: center;
  color: var(--theme-color-eight);
  background: #fff;
  border-radius: 12px;
}

.comment-disabled-state {
  text-align: center;
  color: var(--theme-color-eight);
  font-family: var(--font-bold-type);
}

.comment-editor-frame {
  border: 1px solid var(--theme-color-nine);
  border-radius: 20px;
  overflow: hidden;
  background: #fff;
}

.delete-request-modal {
  background: #ffffff;
  border-radius: 18px;
  padding: 28px 28px 24px;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.18);
}

.delete-request-icon-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}

.delete-request-title {
  text-align: center;
  font-size: 24px;
  line-height: 1.2;
  font-family: var(--font-bold-type);
  color: var(--theme-color-two);
  margin-bottom: 8px;
}

.delete-request-subtitle {
  text-align: center;
  color: var(--theme-color-eight);
  margin-bottom: 18px;
}

@media (max-width: 960px) {
  .topic-head {
    flex-direction: column;
  }
}
</style>
